import { EpisodeHeader, FormGroupContext } from '@ksyos/design-system';
import { connect } from 'react-redux';
import type { EpisodeCategory, Profession } from '@common/enums';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { UseUIStateWrapper } from '@frontend/components/contexts/uiState/UseUIStateWrapper';
import { EpisodeHeaderTabBar } from '@frontend/components/elements/EpisodeHeaderTabBar';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { episodeCategoryIconName } from '@frontend/components/elements/Icons/episodeCategoryIconName';
import { MedicalProtocol } from '@frontend/components/elements/MedicalProtocol';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { translateKey } from '@frontend/translate';
import { isMedicalProtocolAvailable } from '@frontend/utils/medicalProtocolAvailability';
import type { IPatient } from '../../../../common/interfaces/patient';
import type { IReduxState } from '../../../configureStore';
import { AbortableFetch } from '../../../store/fetch';
import { Loading } from '../../elements/common';
import { Component } from '../../elements/Component';
import { PatientAvatarBlock } from '../patients/PatientAvatarBlock';
import { PatientActiveInsuranceContextProvider } from '../patients/PatientInsuranceHistoryContext';
import { PatientSideBar } from '../patients/patientSideBar/PatientSideBar';
import { ContactsAndTasks } from './ContactsAndTasks';

interface IContactsAndTasksReduxProps {
    authentication: {
        organizationId?: string;
        profession?: Profession;
    };
}

interface IContactsAndTasksState {
    patient?: IPatient;
}

const mapStateToProps = ({ authentication }: IReduxState): IContactsAndTasksReduxProps => ({
    authentication: {
        organizationId: authentication.organizationId,
        profession: authentication.profession,
    },
});

export class InnerContactsAndTasksPage extends Component<
    IWithRouterProps & IContactsAndTasksReduxProps,
    IContactsAndTasksState
> {
    private abortableFetch = new AbortableFetch();

    constructor(props: IWithRouterProps & IContactsAndTasksReduxProps, context: unknown) {
        super(props, context);

        this.state = {};
    }

    public async componentDidMount() {
        if (this.props.router.searchParams.get('patientId')) {
            await this.loadPatient();
        } else {
            throw Error('Missing patientId');
        }
    }

    public componentWillUnmount() {
        this.abortableFetch.abort();
    }

    public render() {
        const episodeCategoryName = this.props.router.searchParams.get('episodeCategory') as EpisodeCategory | null;
        if (!episodeCategoryName) {
            return null;
        }

        const isMedicalProtocolPath = this.props.router.location.pathname === '/medicalProtocol';
        const shouldShowMedicalProtocol = isMedicalProtocolPath && isMedicalProtocolAvailable(episodeCategoryName);

        return (
            <Loading
                item={this.state.patient}
                loaded={(patient) => (
                    <PatientActiveInsuranceContextProvider patientId={patient.id}>
                        <PageTemplate
                            noGutterOnMobile={true}
                            sideBarNavigation={
                                <PatientSideBar
                                    episodeCategory={episodeCategoryName}
                                    onReload={async () => await this.loadPatient()}
                                    patient={patient}
                                />
                            }
                            sideBarProfile={
                                <PatientAvatarBlock
                                    patient={patient}
                                    size="large"
                                />
                            }
                            topBarNavigation={
                                <PatientSideBar
                                    episodeCategory={episodeCategoryName}
                                    onReload={async () => await this.loadPatient()}
                                    patient={patient}
                                />
                            }
                            topBarProfile={
                                <PatientAvatarBlock
                                    patient={patient}
                                    size="small"
                                />
                            }
                        >
                            <FormGroupContext.Provider
                                value={{ labelWidth: 'one-quarter', size: 'medium', variant: 'outline' }}
                            >
                                <UseUIStateWrapper>
                                    {(uiState) => {
                                        return (
                                            <>
                                                {this.props.authentication.organizationId && (
                                                    <EpisodeHeader
                                                        bannerHeight={uiState?.bannerHeight}
                                                        icon={episodeCategoryIconName(episodeCategoryName)}
                                                        id="episode-header"
                                                        title={`${translateKey(
                                                            `episodeCategoryEnum.${episodeCategoryName}`
                                                        )}`}
                                                    >
                                                        <EpisodeHeaderTabBar
                                                            episodeCategory={episodeCategoryName}
                                                            organizationId={this.props.authentication.organizationId}
                                                            pathname={this.props.router.location.pathname}
                                                            patientId={patient.id}
                                                        />
                                                    </EpisodeHeader>
                                                )}
                                            </>
                                        );
                                    }}
                                </UseUIStateWrapper>
                                {shouldShowMedicalProtocol ? (
                                    <MedicalProtocol episodeCategoryName={episodeCategoryName} />
                                ) : (
                                    <ContactsAndTasks
                                        episodeCategoryName={episodeCategoryName}
                                        isFlexibleCarePath={false}
                                        patient={patient}
                                    />
                                )}
                            </FormGroupContext.Provider>
                        </PageTemplate>
                    </PatientActiveInsuranceContextProvider>
                )}
            />
        );
    }

    private async loadPatient() {
        const patientId = this.props.router.searchParams.get('patientId');
        const includeItemAuthorizations = [
            'canSeeProfessionalSummaryDataDetails',
            'canSeePatientDetails',
            'canUpdate',
            'canSeePatientAllergies',
            'canSeePatientEpisodes',
            'canSeePatientLabResults',
            'canSeePatientMedication',
            'canUpdatePatientConsent',
        ];
        const params = new URLSearchParams({ includeItemAuthorizations: includeItemAuthorizations.join(',') });
        const url = `/api/patients/${patientId}?${params.toString()}`;
        const result = await this.abortableFetch.fetchJson(url, 'GET');
        await this.setState({ patient: result.json });
    }
}

export const ContactsAndTasksPage = connect(mapStateToProps)(withRouter(InnerContactsAndTasksPage));
